<template>
  <div class="videoChat">
    <div id="videos">
      <div id="video-capture" class="video-capture" v-if="screenCaptureVideo">
        <div class="video-container" :ref="screenCaptureVideo.id">
          <video
            v-if="screenCaptureVideo.id === localUuid"
            :srcObject.prop="screenCaptureVideo.src"
            muted
            autoplay
            playsinline
          />
          <video
            v-if="screenCaptureVideo.id !== localUuid"
            :srcObject.prop="screenCaptureVideo.src"
            autoplay
            playsinline
          />
          <div
            class="videoLabel"
            @click="
              screenCaptureVideo.id === localUuid ? changeChatOptions() : ''
            "
          >
            {{ screenCaptureVideo.displayName }}
          </div>
        </div>
      </div>
      <div
        v-if="videos && videos.length > 0 && !screenCaptureVideo"
        id="video-list"
        class="video-list"
      >
        <div
          class="video-container"
          v-for="video in videos"
          :key="video.id"
          :ref="video.id"
        >
          <video
            v-if="video.id === localUuid"
            :srcObject.prop="video.src"
            muted
            autoplay
            playsinline
          />
          <video
            v-if="video.id !== localUuid"
            :srcObject.prop="video.src"
            autoplay
            playsinline
          />
          <div
            class="videoLabel"
            @click="video.id === localUuid ? changeChatOptions() : ''"
          >
            {{ video.displayName }}
          </div>
        </div>
      </div>
      <div class="videoChat__buttons">
        <b-button
          :title="audioStopped ? 'Unmute microphone' : 'Mute microphone'"
          :type="audioStopped ? 'is-warning' : 'is-success'"
          @click="(audioStopped = !audioStopped), mute('audio', audioStopped)"
          :icon-right="audioStopped ? 'microphone-off' : 'microphone'"
          size="is-medium"
        />
        <b-button
          :title="videoStopped ? 'Unstop camera' : 'Stop camera'"
          :type="videoStopped ? 'is-warning' : 'is-success'"
          @click="(videoStopped = !videoStopped), mute('video', videoStopped)"
          :icon-right="videoStopped ? 'video-off' : 'video'"
          size="is-medium"
        />
        <b-button
          v-if="!isMobileOrTablet"
          :disabled="screenCaptureVideo && screenCaptureVideo.id !== localUuid"
          :title="
            screenCaptureVideo && screenCaptureVideo.id === localUuid
              ? 'Stop screen sharing'
              : 'Share screen'
          "
          :type="
            screenCaptureVideo && screenCaptureVideo.id === localUuid
              ? 'is-warning'
              : 'is-success'
          "
          @click="
            screenCaptureVideo && screenCaptureVideo.id === localUuid
              ? startVideo()
              : startCapture()
          "
          :icon-right="
            screenCaptureVideo && screenCaptureVideo.id === localUuid
              ? 'stop'
              : 'monitor'
          "
          size="is-medium"
        />
        <b-button
          title="Exit video chat"
          type="is-danger"
          size="is-medium"
          @click="stopVideo(), goTo()"
          icon-right="phone-hangup"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import { goTo } from "../../mixins/goTo";
import { generateId, mobileAndTabletCheck } from "../../utils/helpers";

export default {
  name: "videos",
  data: () => {
    return {
      audioStopped: false,
      videoStopped: false,
      screenCaptureVideo: null,
      isMobileOrTablet: false,
      videos: null,
      screenConstraints: {
        video: {
          height: 900,
          resizeMode: "crop-and-scale",
          width: 1600,
          frameRate: 5
        },
        audio: true
      },
      cameraConstraints: {
        video: {
          width: 320,
          height: 240,
          frameRate: 20,
          facingMode: "user"
        },
        audio: true
      },
      localUuid: "",
      screenCaptureUuid: "",
      localDisplayName: "",
      localStream: "",
      localVideo: "",
      serverConnection: "",
      peerConnections: {}, // key is uuid, values are peer connection object and user defined display name string
      peerConnectionConfig: {
        iceServers: [
          {
            urls: ["stun:stun.l.google.com:19302", "stun:eu-turn4.xirsys.com"]
          },
          {
            username:
              "8LavIsmjhUaKgS_SY0_PXYRDVm7no6-CtQ5Y1gNvnhr27Ki8lTGs3b1Zn1e7FueeAAAAAF76U7NwYmFiZXk=",
            credential: "f0653bb0-ba49-11ea-8ccd-0242ac140004",
            urls: [
              "turn:eu-turn4.xirsys.com:80?transport=udp",
              "turn:eu-turn4.xirsys.com:3478?transport=udp",
              "turn:eu-turn4.xirsys.com:80?transport=tcp",
              "turn:eu-turn4.xirsys.com:3478?transport=tcp",
              "turns:eu-turn4.xirsys.com:443?transport=tcp",
              "turns:eu-turn4.xirsys.com:5349?transport=tcp"
            ]
          }
        ]
      }
    };
  },
  mixins: [goTo],
  props: {
    subscribeId: {
      type: String,
      default: "415d2973-592f-4a5e-8851-e401b40ddf85"
    },
    chatOptions: {
      type: Object,
      default: null
    }
  },
  methods: {
    ...mapMutations(["setChatOptions"]),
    changeChatOptions() {
      this.dialog = this.$buefy.dialog.prompt({
        title: "Video Chat",
        message: `Change your name`,
        inputAttrs: {
          placeholder: "e.g. Pascal",
          maxlength: 20
        },
        trapFocus: true,
        confirmText: "Ok",
        // eslint-disable-next-line no-unused-vars
        onConfirm: value => {
          this.localDisplayName = value;
          this.setChatOptions({
            name: value
          });
        }
      });
    },
    gotMessageFromServer(message) {
      var signal = JSON.parse(message);
      var peerUuid = signal.uuid;

      if (signal.dest === "left") {
        if (
          this.peerConnections[peerUuid] &&
          this.peerConnections[peerUuid].pc
        ) {
          this.peerConnections[peerUuid].pc.close();
        }

        if (this.videos) {
          this.videos = this.videos.filter(video => {
            return video.id !== peerUuid;
          });
        }

        if (this.screenCaptureVideo) {
          this.screenCaptureVideo =
            this.screenCaptureVideo.id === peerUuid
              ? null
              : this.screenCaptureVideo;
        }
      }

      // Ignore messages that are not for us or from ourselves
      if (
        peerUuid == this.localUuid ||
        (signal.dest != this.localUuid && signal.dest != "all")
      )
        return;

      if (signal.displayName && signal.dest == "all") {
        const isScreenCapture =
          this.screenCaptureVideo &&
          this.screenCaptureVideo.id === this.localUuid;
        // set up peer connection object for a newcomer peer
        this.setUpPeer(peerUuid, signal.displayName, signal.type);
        this.$socket.emit(
          process.env.VUE_APP_SOCKET_ON,
          this.subscribeId,
          JSON.stringify({
            displayName: this.localDisplayName,
            uuid: this.localUuid,
            dest: peerUuid,
            type: isScreenCapture ? "screen-capture" : null
          })
        );
      } else if (signal.displayName && signal.dest == this.localUuid) {
        // initiate call if we are the newcomer peer
        this.setUpPeer(peerUuid, signal.displayName, signal.type, true);
      } else if (signal.sdp) {
        const _this = this;
        this.peerConnections[peerUuid].pc
          .setRemoteDescription(new RTCSessionDescription(signal.sdp))
          .then(function() {
            // Only create answers in response to offers
            if (signal.sdp.type == "offer") {
              _this.peerConnections[peerUuid].pc
                .createAnswer()
                .then(description =>
                  _this.createdDescription(description, peerUuid, signal.type)
                )
                .catch(_this.errorHandler);
            }
          })
          .catch(this.errorHandler);
      } else if (signal.ice) {
        this.peerConnections[peerUuid].pc
          .addIceCandidate(new RTCIceCandidate(signal.ice))
          .catch(this.errorHandler);
      }
    },
    mute(kind, stop) {
      if (this.localStream) {
        this.localStream.getTracks().map(t => {
          if (t.kind === kind) {
            t.enabled = !stop;
          }
        });
      }
    },
    stopTracks(stream) {
      if (stream) {
        stream.getTracks().map(t => {
          t.enabled = !stop;
          t.stop();
        });
      }
    },
    stopVideo() {
      this.stopTracks(this.localStream);
      if (this.screenCaptureVideo) {
        this.stopTracks(this.screenCaptureVideo.src);
      }

      if (this.videos && this.videos.length > 0) {
        this.videos.map(video => {
          this.stopTracks(video.src);
        });
      }

      this.$socket.emit(
        process.env.VUE_APP_SOCKET_ON,
        this.subscribeId,
        JSON.stringify({
          displayName: this.localDisplayName,
          uuid: this.localUuid,
          dest: "left"
        })
      );
    },
    startCapture() {
      this.peerConnections[this.localUuid] = {
        displayName: this.localDisplayName,
        type: "screen-capture"
      };
      return navigator.mediaDevices
        .getDisplayMedia(this.screenConstraints)
        .then(stream => {
          if (this.localStream) {
            this.localStream.getTracks().map(track => {
              if (track.kind === "video") {
                track.stop();
                this.localStream.removeTrack(track);
              }
            });

            stream.getTracks().map(track => {
              this.localStream.addTrack(track);
            });
          } else {
            this.localStream = stream;
          }
          if (this.videos) {
            this.videos = this.videos.filter(video => {
              return video.id !== this.localUuid;
            });
          }
          this.addStreamToContainer(
            stream,
            this.localUuid,
            "screen-capture",
            this.localDisplayName
          );
        })
        .catch(this.errorHandler)
        .then(() => {
          this.$socket.emit(
            process.env.VUE_APP_SOCKET_ON,
            this.subscribeId,
            JSON.stringify({
              displayName: this.localDisplayName,
              uuid: this.localUuid,
              dest: "all",
              type: "screen-capture"
            })
          );
        })
        .catch(this.errorHandler);
    },
    startVideo() {
      this.peerConnections[this.localUuid] = {
        displayName: this.localDisplayName,
        type: null
      };
      // set up local video stream
      if (navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices
          .getUserMedia(this.cameraConstraints)
          .then(stream => {
            if (this.localStream) {
              this.localStream.getTracks().map(track => {
                track.stop();
                this.localStream.removeTrack(track);
              });

              stream.getTracks().map(track => {
                this.localStream.addTrack(track);
              });
            } else {
              this.localStream = stream;
            }
            // this.mute("video", true);
            this.addStreamToContainer(
              stream,
              this.localUuid,
              null,
              this.localDisplayName
            );
          })
          .catch(this.errorHandler)

          // set up websocket and message all existing clients
          .then(() => {
            this.$socket.emit(
              process.env.VUE_APP_SOCKET_ON,
              this.subscribeId,
              JSON.stringify({
                displayName: this.localDisplayName,
                uuid: this.localUuid,
                dest: "all"
              })
            );

            this.sockets.subscribe(this.subscribeId, msg => {
              this.gotMessageFromServer(msg);
            });
          })
          .catch(this.errorHandler);
      } else {
        alert("Your browser does not support getUserMedia API");
      }
    },
    setUpPeer(peerUuid, displayName, type, initCall = false) {
      this.peerConnections[peerUuid] = {
        displayName: displayName,
        pc: new RTCPeerConnection(this.peerConnectionConfig),
        type: type
      };
      this.peerConnections[peerUuid].pc.onicecandidate = event =>
        this.gotIceCandidate(event, peerUuid, type);
      this.peerConnections[peerUuid].pc.ontrack = event =>
        this.gotRemoteStream(event, peerUuid, type);
      this.peerConnections[peerUuid].pc.oniceconnectionstatechange = event =>
        this.checkPeerDisconnect(event, peerUuid);
      this.peerConnections[peerUuid].pc.addStream(this.localStream);

      if (initCall) {
        this.peerConnections[peerUuid].pc
          .createOffer()
          .then(description =>
            this.createdDescription(description, peerUuid, type)
          )
          .catch(this.errorHandler);
      }
    },
    gotIceCandidate(event, peerUuid, type) {
      if (event.candidate != null) {
        this.$socket.emit(
          process.env.VUE_APP_SOCKET_ON,
          this.subscribeId,
          JSON.stringify({
            ice: event.candidate,
            uuid: this.localUuid,
            dest: peerUuid,
            type: type
          })
        );
      }
    },
    createdDescription(description, peerUuid, type) {
      const _this = this;
      console.log(`got description, peer ${peerUuid}`);
      this.peerConnections[peerUuid].pc
        .setLocalDescription(description)
        .then(function() {
          _this.$socket.emit(
            process.env.VUE_APP_SOCKET_ON,
            _this.subscribeId,
            JSON.stringify({
              sdp: _this.peerConnections[peerUuid].pc.localDescription,
              uuid: _this.localUuid,
              dest: peerUuid,
              type: type
            })
          );
        })
        .catch(this.errorHandler);
    },
    addStreamToContainer(src, peerUuid, type, displayName) {
      const newVideo = {
        src: src,
        id: peerUuid,
        displayName: displayName,
        type: type
      };
      if (type && type === "screen-capture") {
        if (this.videos) {
          this.videos = this.videos.filter(video => {
            return video.id !== newVideo.id;
          });
        }
        this.screenCaptureVideo = newVideo;
      } else {
        if (
          this.screenCaptureVideo &&
          this.screenCaptureVideo.id === newVideo.id
        ) {
          this.stopTracks(this.screenCaptureVideo.src);

          this.screenCaptureVideo = null;
        }

        if (!this.videos) {
          this.videos = [];
          this.videos.push(newVideo);
        } else {
          let video = this.videos.find(video => {
            return video.id === newVideo.id;
          });

          if (!video) {
            this.videos.push(newVideo);
          }
        }
      }
    },
    gotRemoteStream(event, peerUuid, type) {
      console.log(`got remote stream, peer ${peerUuid}`);
      this.addStreamToContainer(
        event.streams[0],
        peerUuid,
        type,
        this.peerConnections[peerUuid].displayName
      );
    },
    checkPeerDisconnect(event, peerUuid) {
      if (this.peerConnections[peerUuid]) {
        var state = this.peerConnections[peerUuid].pc.iceConnectionState;
        console.log(`connection with peer ${peerUuid} ${state}`);
        if (
          state === "failed" ||
          state === "closed" ||
          state === "disconnected"
        ) {
          delete this.peerConnections[peerUuid];
        }
      }
    },
    makeLabel(label) {
      var vidLabel = document.createElement("div");
      vidLabel.appendChild(document.createTextNode(label));
      vidLabel.setAttribute("class", "videoLabel");
      return vidLabel;
    },
    errorHandler(error) {
      console.log(error);
    }
  },
  beforeDestroy() {
    this.sockets.unsubscribe(this.subscribeId);
    this.stopVideo();
  },
  mounted() {
    this.isMobileOrTablet = mobileAndTabletCheck();
    this.localUuid = generateId();
    this.screenCaptureUuid = generateId();
    this.localDisplayName = this.user
      ? this.user.username
      : this.chatOptions.name || "Guest";

    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.log("enumerateDevices() not supported.");
      return;
    }

    this.startVideo();
  }
};
</script>
